import type { LoLearnerGamificationProfile, LoLearnerGamificationSummary } from '@/lib/learner-gamification';
import type { LoPlanUsage } from '@/lib/lyrraone-plan';
import { api } from '@/lib/api';
import { unwrapApiData } from '@/lib/store-formation';

export interface LoContact {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  mobilePhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  source?: string | null;
  consentEmail: boolean;
  consentSms: boolean;
  tags?: Array<{ tag: { id: string; name: string; color: string } }>;
  createdAt: string;
}

export type LoContactExtraInput = {
  phone?: string | null;
  mobilePhone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  source?: string;
};

export interface LoOverview {
  contacts: number;
  funnels: number;
  campaigns: number;
  sequences: number;
  automations: number;
  site: { id: string; status: string; slug: string } | null;
  bioLinks: number;
  pipeline: { id: string } | null;
  affiliateProgram: { isActive: boolean } | null;
  bookingTypes: number;
  communities: number;
  webinars: number;
  physicalProducts: number;
  smsMessages: number;
  formations: number;
  blogPosts: number;
}

async function hubGet<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const resp = await api.get(`/lyrraone${path}`, params ? { params } : undefined);
  return unwrapApiData<T>(resp);
}

async function hubPost<T>(path: string, body?: unknown): Promise<T> {
  const resp = await api.post(`/lyrraone${path}`, body);
  return unwrapApiData<T>(resp);
}

async function hubPatch<T>(path: string, body: unknown): Promise<T> {
  const resp = await api.patch(`/lyrraone${path}`, body);
  return unwrapApiData<T>(resp);
}

async function hubPut<T>(path: string, body: unknown): Promise<T> {
  const resp = await api.put(`/lyrraone${path}`, body);
  return unwrapApiData<T>(resp);
}

async function hubDelete<T>(path: string): Promise<T> {
  const resp = await api.delete(`/lyrraone${path}`);
  return unwrapApiData<T>(resp);
}

export const fetchLoOverview = () => hubGet<LoOverview>('/overview');

export type LoAnalyticsPeriod = '7d' | '30d' | '90d' | '365d';

export interface LoAnalyticsPayload {
  period: { from: string; to: string };
  detailLevel?: 'basic' | 'full';
  kpis: {
    revenueCents: number;
    salesCount: number;
    newContacts: number;
    totalContacts: number;
    emailsSent: number;
    uniqueLearners: number;
    learnersCompleted: number;
    bioLinkClicks: number;
    funnelViews: number;
    funnelLeads: number;
    funnelPurchases: number;
    publishedFunnels: number;
    publishedFormations: number;
  };
  revenueSeries: Array<{ name: string; revenue: number; sales: number }>;
  contactsSeries: Array<{ name: string; contacts: number }>;
  emailsSeries: Array<{ name: string; emails: number }>;
  topFunnels: Array<{ name: string; views: number; leads: number; purchases: number }>;
  learnerStatus: Array<{ name: string; value: number }>;
  conversionFunnel: Array<{ stage: string; value: number }>;
}

export const fetchLoAnalytics = (period: LoAnalyticsPeriod = '30d') =>
  hubGet<LoAnalyticsPayload>(`/analytics?period=${encodeURIComponent(period)}`);

export const fetchLoContacts = () => hubGet<LoContact[]>('/contacts');
export const createLoContact = (data: {
  email: string;
  firstName?: string;
  lastName?: string;
  consentEmail?: boolean;
  consentSms?: boolean;
} & LoContactExtraInput) => hubPost<LoContact>('/contacts', data);
export const updateLoContact = (id: string, data: {
  firstName?: string;
  lastName?: string;
  consentEmail?: boolean;
  consentSms?: boolean;
} & Partial<LoContactExtraInput>) => hubPatch<LoContact>(`/contacts/${encodeURIComponent(id)}`, data);
export const deleteLoContact = (id: string) => hubDelete<{ deleted: boolean }>(`/contacts/${id}`);
export const sendLoContactSms = (
  contactId: string,
  data: { body: string },
) => hubPost<{ sent: boolean; channel: 'sms' }>(`/contacts/${encodeURIComponent(contactId)}/sms`, data);

export const sendLoContactEmail = (
  contactId: string,
  data: { subject: string; htmlBody: string },
) => hubPost<{ sent: boolean; channel: 'email'; queued?: boolean }>(
  `/contacts/${encodeURIComponent(contactId)}/email`,
  data,
);

export interface LoTag {
  id: string;
  name: string;
  color: string;
}

export const fetchLoTags = () => hubGet<LoTag[]>('/tags');
export const addLoContactTag = (contactId: string, tagName: string) =>
  hubPost<LoContact>(`/contacts/${encodeURIComponent(contactId)}/tags`, { tagName });

export const addLoContactsTagBulk = (contactIds: string[], tagName: string) =>
  hubPost<{
    tag: LoTag;
    requested: number;
    matched: number;
    tagged: number;
    alreadyHadTag: number;
  }>('/contacts/tags/bulk', { contactIds, tagName });

export interface LoContactActivity {
  id: string;
  type: string;
  title: string;
  detail?: string | null;
  createdAt: string;
}

export const fetchLoContact = (contactId: string) =>
  hubGet<LoContact>(`/contacts/${encodeURIComponent(contactId)}`);

export const fetchLoContactActivities = (contactId: string) =>
  hubGet<LoContactActivity[]>(`/contacts/${encodeURIComponent(contactId)}/activities`);

export const createLoContactActivity = (
  contactId: string,
  data: { type: 'note' | 'call' | 'email' | 'sms'; title?: string; detail: string },
) => hubPost<LoContactActivity>(`/contacts/${encodeURIComponent(contactId)}/activities`, data);

export interface LoLearnerFormationOverview {
  id: string;
  storeFormationId?: string | null;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  categoryName?: string | null;
  instructor: string;
  estimatedDuration?: number | null;
  purchasedAt?: string | null;
  source: 'store' | 'assigned';
  progress: number;
  completed: boolean;
  totalTimeSpent: number;
  lastAccessAt?: string | null;
  completedAt?: string | null;
  objectives?: unknown;
}

export const fetchLoLearnerOverview = () =>
  hubGet<{
    formations: LoLearnerFormationOverview[];
    purchasedCount: number;
    gamification: LoLearnerGamificationSummary;
  }>('/learner/overview');

export const fetchLoLearnerGamification = () =>
  hubGet<LoLearnerGamificationProfile>('/learner/gamification');

export interface LoLearnerCommunityOverview {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  heroImageUrl: string | null;
  accessType: string;
  memberCount: number;
  postCount: number;
  /** true si l’apprenant est déjà membre */
  isMember?: boolean;
  joinedAt: string | null;
  creatorName: string;
}

export const fetchLoLearnerCommunities = () =>
  hubGet<LoLearnerCommunityOverview[]>('/learner/communities');

export type FormationLeaderboardEntry = {
  rank: number;
  userId: string | null;
  displayName: string;
  isSelf: boolean;
  xp: number;
  completionPercentage: number;
  completed: boolean;
};

export type FormationLeaderboardPayload = {
  flowId: string;
  flowTitle: string;
  enabled: boolean;
  publicNames?: boolean;
  topN?: number;
  entries: FormationLeaderboardEntry[];
  viewerRank: number | null;
  viewerXp: number;
};

export const fetchFormationLeaderboard = (flowId: string) =>
  hubGet<FormationLeaderboardPayload>(`/learner/formation/${encodeURIComponent(flowId)}/leaderboard`);

export async function exportLoContactsCsv(): Promise<void> {
  await downloadLyrraOneBlob('/contacts/export', 'contacts-lyrraone.csv', 'text/csv;charset=utf-8');
}

async function downloadLyrraOneBlob(
  path: string,
  filename: string,
  mimeType = 'application/octet-stream',
): Promise<void> {
  // api.get(url, params?, axiosConfig?) — responseType doit être en 3ᵉ argument
  const data = await api.get<Blob>(`/lyrraone${path}`, undefined, { responseType: 'blob' });
  const blob = data instanceof Blob
    ? data
    : new Blob([data as unknown as BlobPart], { type: mimeType });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const exportLoContactGdprJson = (contactId: string) =>
  downloadLyrraOneBlob(
    `/contacts/${contactId}/gdpr-export`,
    `contact-${contactId}-gdpr.json`,
    'application/json;charset=utf-8',
  );

export const exportLoContactsGdprJson = () =>
  downloadLyrraOneBlob(
    '/contacts/gdpr-export',
    `contacts-gdpr-${Date.now()}.json`,
    'application/json;charset=utf-8',
  );

export interface LoCreatorFormation {
  id: string;
  title: string;
  price: number;
  salesCount: number;
}

export const fetchLoCreatorFormations = () => hubGet<LoCreatorFormation[]>('/formations');

export const fetchLoCampaigns = () => hubGet<Array<{
  id: string;
  name: string;
  subject: string;
  status: string;
  sentAt?: string | null;
  scheduledAt?: string | null;
  stats?: {
    sent?: number;
    failed?: number;
    total?: number;
    opens?: number;
    clicks?: number;
    uniqueOpens?: number;
    uniqueClicks?: number;
  };
}>>('/email/campaigns');
export const createLoCampaign = (data: {
  name: string;
  subject: string;
  htmlBody: string;
  segmentId?: string;
}) => hubPost('/email/campaigns', data);
export const sendLoCampaign = (id: string) => hubPost(`/email/campaigns/${id}/send`);

export interface LoEmailCampaignDetail {
  id: string;
  name: string;
  subject: string;
  htmlBody: string;
  status: string;
  segmentId?: string | null;
  scheduledAt?: string | null;
  stats?: Record<string, number>;
}

export const fetchLoCampaign = (id: string) => hubGet<LoEmailCampaignDetail>(`/email/campaigns/${id}`);

export const updateLoCampaign = (
  id: string,
  data: {
    name?: string;
    subject?: string;
    htmlBody?: string;
    segmentId?: string | null;
    status?: string;
    scheduledAt?: string | null;
  },
) => hubPatch<LoEmailCampaignDetail>(`/email/campaigns/${id}`, data);

export const scheduleLoCampaign = (id: string, scheduledAt: string) =>
  hubPost<LoEmailCampaignDetail>(`/email/campaigns/${id}/schedule`, { scheduledAt });

export interface LoEmailTemplate {
  id: string;
  name: string;
  category: string;
  subject: string;
  htmlBody: string;
  blocks?: unknown[];
  isSystem?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const fetchLoEmailTemplates = (category?: string) => {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  return hubGet<LoEmailTemplate[]>(`/email/templates${q}`);
};

export const fetchLoEmailTemplate = (id: string) => hubGet<LoEmailTemplate>(`/email/templates/${id}`);

export const createLoEmailTemplate = (data: {
  name: string;
  category?: string;
  subject: string;
  htmlBody: string;
  blocks?: unknown[];
}) => hubPost<LoEmailTemplate>('/email/templates', data);

export const updateLoEmailTemplate = (
  id: string,
  data: { name?: string; category?: string; subject?: string; htmlBody?: string; blocks?: unknown[] },
) => hubPatch<LoEmailTemplate>(`/email/templates/${id}`, data);

export const deleteLoEmailTemplate = (id: string) => hubDelete<{ ok: true }>(`/email/templates/${id}`);

/* ——— Templates SMS ——— */

export interface LoSmsTemplate {
  id: string;
  name: string;
  category: string;
  body: string;
  isSystem?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const fetchLoSmsTemplates = (category?: string) => {
  const q = category ? `?category=${encodeURIComponent(category)}` : '';
  return hubGet<LoSmsTemplate[]>(`/sms/templates${q}`);
};

export const fetchLoSmsTemplate = (id: string) => hubGet<LoSmsTemplate>(`/sms/templates/${id}`);

export const createLoSmsTemplate = (data: {
  name: string;
  category?: string;
  body: string;
}) => hubPost<LoSmsTemplate>('/sms/templates', data);

export const updateLoSmsTemplate = (
  id: string,
  data: Partial<{ name: string; category: string; body: string }>,
) => hubPatch<LoSmsTemplate>(`/sms/templates/${id}`, data);

export const deleteLoSmsTemplate = (id: string) => hubDelete<{ ok: true }>(`/sms/templates/${id}`);

export interface LoSmtpSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  fromEmail: string;
  fromName: string;
  hasPassword: boolean;
  configured: boolean;
}

export const fetchLoSmtpSettings = () => hubGet<LoSmtpSettings>('/email/smtp');
export const updateLoSmtpSettings = (data: {
  host?: string;
  port?: number;
  secure?: boolean;
  user?: string;
  password?: string;
  fromEmail?: string;
  fromName?: string;
}) => hubPut<LoSmtpSettings>('/email/smtp', data);
export const testLoSmtpSettings = (to: string) => hubPost<{ ok: boolean }>('/email/smtp/test', { to });

export interface LoSegment {
  id: string;
  name: string;
  filters: {
    tagNames?: string[];
    excludeTagNames?: string[];
    sources?: string[];
    requireConsentEmail?: boolean;
    purchasedFormationId?: string;
    excludeClients?: boolean;
    openedCampaignId?: string;
    notOpenedCampaignId?: string;
  };
  contactCount?: number;
}

export const fetchLoSegments = () => hubGet<LoSegment[]>('/segments');
export const createLoSegment = (data: {
  name: string;
  filters?: LoSegment['filters'];
}) => hubPost<LoSegment>('/segments', data);
export const deleteLoSegment = (id: string) => hubDelete<{ deleted: boolean }>(`/segments/${id}`);

export const recordLoCheckoutIntent = (funnelSlug: string) =>
  hubPost<{ ok: boolean }>('/checkout-intent', { funnelSlug });

export type LoContactCsvField =
  | 'email'
  | 'firstName'
  | 'lastName'
  | 'fullName'
  | 'phone'
  | 'mobilePhone'
  | 'addressLine1'
  | 'addressLine2'
  | 'postalCode'
  | 'city'
  | 'country'
  | 'source';

export type LoContactCsvFieldMapping = Partial<Record<LoContactCsvField, number>>;

export const importLoContactsCsv = (
  csv: string,
  options?: {
    tagNames?: string[];
    consentEmail?: boolean;
    fieldMapping?: LoContactCsvFieldMapping;
  },
) =>
  hubPost<{ imported: number; skipped: number; skippedDuplicates?: number; total: number }>('/contacts/import', {
    csv,
    tagNames: options?.tagNames,
    consentEmail: options?.consentEmail ? true as const : undefined,
    fieldMapping: options?.fieldMapping,
  });

export const enrollLoSequence = (sequenceId: string, data: { contactId?: string; email?: string }) =>
  hubPost(`/email/sequences/${sequenceId}/enroll`, data);

export const addLoSequenceStep = (sequenceId: string, data: { subject: string; htmlBody: string; delayDays: number }) =>
  hubPost(`/email/sequences/${sequenceId}/steps`, data);

export type LoSequenceStep = {
  id: string;
  order: number;
  subject: string;
  delayDays: number;
  htmlBody: string;
};

export type LoSequence = {
  id: string;
  name: string;
  status: string;
  steps: LoSequenceStep[];
};

export const updateLoSequenceStep = (
  sequenceId: string,
  stepId: string,
  data: { subject?: string; htmlBody?: string; delayDays?: number },
) => hubPatch<LoSequenceStep>(`/email/sequences/${sequenceId}/steps/${stepId}`, data);

export const deleteLoSequenceStep = (sequenceId: string, stepId: string) =>
  hubDelete<{ ok: true }>(`/email/sequences/${sequenceId}/steps/${stepId}`);

export const fetchLoSequences = () => hubGet<LoSequence[]>('/email/sequences');
export const createLoSequence = (name: string) => hubPost('/email/sequences', { name });

export const fetchLoAutomations = () => hubGet<LoAutomationDetail[]>('/automations');

export interface LoAutomationDetail {
  id: string;
  name: string;
  status: 'draft' | 'active' | 'paused' | string;
  triggerType: string;
  runCount: number;
  triggerConfig?: Record<string, unknown> | null;
  conditions?: unknown;
  actions?: unknown;
  createdAt?: string;
  updatedAt?: string;
}

export const fetchLoAutomation = (id: string) => hubGet<LoAutomationDetail>(`/automations/${id}`);

export interface LoAutomationTemplate {
  id: string;
  name: string;
  description: string;
  triggerType: string;
  stepCount: number;
}

export const fetchLoAutomationTemplates = () => hubGet<LoAutomationTemplate[]>('/automations/templates');
export const installLoAutomationTemplate = (templateId: string) =>
  hubPost('/automations/from-template', { templateId });

export const createLoAutomation = (data: {
  name: string;
  triggerType: string;
  triggerConfig?: { sequenceId?: string };
  status?: 'draft' | 'active' | 'paused';
  conditions?: unknown[];
  actions?: unknown[];
}) => hubPost('/automations', data);
export const updateLoAutomation = (
  id: string,
  data: {
    status?: 'draft' | 'active' | 'paused';
    name?: string;
    triggerType?: string;
    triggerConfig?: { sequenceId?: string } | Record<string, unknown>;
    conditions?: unknown[];
    actions?: unknown[];
  },
) => hubPatch(`/automations/${id}`, data);
export const deleteLoAutomation = (id: string) => hubDelete(`/automations/${id}`);

export interface LoAutomationRun {
  id: string;
  triggerType: string;
  status: string;
  errorMessage?: string | null;
  ranAt: string;
  automation?: { id: string; name: string };
  contact?: { email: string; firstName?: string | null; lastName?: string | null };
}

export const fetchLoAutomationRuns = (limit = 30) =>
  hubGet<LoAutomationRun[]>(`/automations/runs?limit=${limit}`);

export const fetchLoAutomationRunsForAutomation = (automationId: string, limit = 20) =>
  hubGet<LoAutomationRun[]>(`/automations/${automationId}/runs?limit=${limit}`);

export const fetchLoSite = () => hubGet<{
  id: string;
  name: string;
  slug: string;
  status: string;
  settings?: Record<string, unknown>;
  pages: Array<{
    id: string;
    title: string;
    slug: string;
    status: string;
    isHome: boolean;
    order: number;
    seoTitle?: string | null;
    seoDescription?: string | null;
    content?: unknown;
  }>;
}>('/site');
export const updateLoSite = (data: {
  name?: string;
  status?: string;
  settings?: Record<string, unknown>;
}) => hubPatch<{
  id: string;
  name: string;
  slug: string;
  status: string;
  settings?: Record<string, unknown>;
  pages?: unknown[];
}>('/site', data);
export const createLoSitePage = (data: { title: string }) => hubPost('/site/pages', data);
export const updateLoSitePage = (
  pageId: string,
  data: {
    title?: string;
    content?: unknown[];
    status?: string;
    seoTitle?: string | null;
    seoDescription?: string | null;
  },
) => hubPatch(`/site/pages/${pageId}`, data);
export const deleteLoSitePage = (pageId: string) =>
  hubDelete<{ deleted: boolean }>(`/site/pages/${pageId}`);
export const reorderLoSitePages = (pageIds: string[]) =>
  hubPut('/site/pages/reorder', { pageIds });
export const fetchLoSiteTemplates = () =>
  hubGet<Array<{
    id: string;
    name: string;
    description: string;
    category: string;
    tags: string[];
    pageCount: number;
    themeId: string;
    preview: { accent: string; accentSoft: string; heroTone: 'light' | 'dark' | 'gradient'; thumbnailUrl?: string };
    homePreview: Array<Record<string, unknown>>;
  }>>('/site/templates');
export const applyLoSiteTemplate = (templateId: string) =>
  hubPost(`/site/templates/${templateId}/apply`);

export interface LoBlogCategory {
  id: string;
  name: string;
  slug: string;
  order: number;
  _count?: { posts: number };
}

export interface LoBlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  body: string;
  coverImageUrl?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  status: string;
  publishedAt?: string | null;
  category?: { id: string; name: string; slug: string } | null;
}

export const fetchLoBlogCategories = () => hubGet<LoBlogCategory[]>('/site/blog/categories');
export const createLoBlogCategory = (data: { name: string; slug?: string }) =>
  hubPost<LoBlogCategory>('/site/blog/categories', data);
export const updateLoBlogCategory = (id: string, data: Partial<{ name: string; slug: string; order: number }>) =>
  hubPatch<LoBlogCategory>(`/site/blog/categories/${id}`, data);
export const deleteLoBlogCategory = (id: string) =>
  hubDelete<{ deleted: boolean }>(`/site/blog/categories/${id}`);
export const fetchLoBlogPosts = () => hubGet<LoBlogPost[]>('/site/blog/posts');
export const createLoBlogPost = (data: Partial<LoBlogPost> & { title: string }) =>
  hubPost<LoBlogPost>('/site/blog/posts', data);
export const updateLoBlogPost = (id: string, data: Partial<LoBlogPost>) =>
  hubPatch<LoBlogPost>(`/site/blog/posts/${id}`, data);
export const deleteLoBlogPost = (id: string) =>
  hubDelete<{ deleted: boolean }>(`/site/blog/posts/${id}`);

export type {
  LoAcquisitionForm,
  LoAcquisitionFormField,
  LoAcquisitionFormSettings,
} from '@/lib/lyrraone-acquisition-form';

export const fetchLoAcquisitionForms = () =>
  hubGet<import('@/lib/lyrraone-acquisition-form').LoAcquisitionForm[]>('/acquisition-forms');

export const fetchLoAcquisitionForm = (id: string) =>
  hubGet<import('@/lib/lyrraone-acquisition-form').LoAcquisitionForm>(
    `/acquisition-forms/${encodeURIComponent(id)}`,
  );

export type LoAcquisitionFormSubmission = {
  id: string;
  submittedAt: string;
  contactId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  consentEmail: boolean;
  answers: Record<string, string>;
};

export const fetchLoAcquisitionFormSubmissions = (id: string) =>
  hubGet<{
    formId: string;
    count: number;
    submissionCount: number;
    items: LoAcquisitionFormSubmission[];
  }>(`/acquisition-forms/${encodeURIComponent(id)}/submissions`);

export const createLoAcquisitionForm = (data: {
  name: string;
  slug?: string;
  fields?: import('@/lib/lyrraone-acquisition-form').LoAcquisitionFormField[];
  settings?: Partial<import('@/lib/lyrraone-acquisition-form').LoAcquisitionFormSettings>;
}) => hubPost<import('@/lib/lyrraone-acquisition-form').LoAcquisitionForm>('/acquisition-forms', data);

export const updateLoAcquisitionForm = (
  id: string,
  data: Partial<{
    name: string;
    slug: string;
    status: 'draft' | 'published';
    fields: import('@/lib/lyrraone-acquisition-form').LoAcquisitionFormField[];
    settings: Partial<import('@/lib/lyrraone-acquisition-form').LoAcquisitionFormSettings>;
  }>,
) => hubPatch<import('@/lib/lyrraone-acquisition-form').LoAcquisitionForm>(`/acquisition-forms/${encodeURIComponent(id)}`, data);

export const deleteLoAcquisitionForm = (id: string) =>
  hubDelete<{ ok: boolean }>(`/acquisition-forms/${encodeURIComponent(id)}`);

export const updateLoCreatorPageTheme = (theme: string) =>
  hubPatch('/site/creator-page', { theme });

export const prepareLoCustomDomain = (domain: string) =>
  hubPost<{
    domain: string;
    txtHost: string;
    txtValue: string;
    cnameHost?: string;
    cnameTarget?: string;
    cnameFullHost?: string;
    instructions?: string[];
  }>('/site/custom-domain', { domain });

export const fetchLoCustomDomainDns = () =>
  hubGet<{
    domain: string;
    txtHost: string;
    txtValue: string;
    cnameHost?: string;
    cnameTarget?: string;
    cnameFullHost?: string;
    instructions?: string[];
    verified?: boolean;
  }>('/site/custom-domain');

export interface LoFormationBundle {
  id: string;
  title: string;
  description?: string | null;
  slug: string;
  coverImageUrl?: string | null;
  price: number;
  isPublished: boolean;
  salesCount: number;
  totalRevenue: number;
  formationIds: string[];
  formations: Array<{ id: string; title: string; price: number; coverImageUrl?: string | null }>;
}

export const fetchLoFormationBundles = () => hubGet<LoFormationBundle[]>('/formation-bundles');

export const createLoFormationBundle = (data: {
  title: string;
  description?: string;
  slug?: string;
  price: number;
  coverImageUrl?: string;
  formationIds: string[];
}) => hubPost<LoFormationBundle>('/formation-bundles', data);

export const updateLoFormationBundle = (
  id: string,
  data: Partial<{
    title: string;
    description: string | null;
    slug: string;
    price: number;
    coverImageUrl: string | null;
    isPublished: boolean;
    formationIds: string[];
  }>,
) => hubPatch<LoFormationBundle>(`/formation-bundles/${id}`, data);

export const deleteLoFormationBundle = (id: string) =>
  hubDelete<{ deleted: boolean }>(`/formation-bundles/${id}`);

export const purchaseLoFormationBundle = (id: string) =>
  hubPost<unknown>(`/formation-bundles/${id}/purchase`);

export const verifyLoCustomDomain = () =>
  hubPost<{ verified: boolean; domain: string }>('/site/custom-domain/verify');

export type LoSiteSslStatus = {
  mode: 'letsencrypt' | 'custom';
  status: 'none' | 'pending' | 'active' | 'error';
  issuer?: string | null;
  expiresAt?: string | null;
  lastCheckedAt?: string | null;
  lastError?: string | null;
  customCertUploadedAt?: string | null;
  customCertSubject?: string | null;
  customCertProvisioned?: boolean;
  domain: string | null;
  domainVerified: boolean;
  customProvisioningAvailable: boolean;
  httpsUrl: string | null;
};

export const fetchLoSiteSslStatus = () =>
  hubGet<LoSiteSslStatus>('/site/custom-domain/ssl');

export const refreshLoSiteSslStatus = () =>
  hubPost<LoSiteSslStatus>('/site/custom-domain/ssl/refresh');

export const enableLoSiteLetsEncrypt = () =>
  hubPost<LoSiteSslStatus>('/site/custom-domain/ssl/letsencrypt');

export const uploadLoSiteCustomSsl = (data: {
  certificatePem: string;
  privateKeyPem: string;
  chainPem?: string;
}) => hubPost<LoSiteSslStatus>('/site/custom-domain/ssl/custom', data);

export const removeLoSiteCustomSsl = () =>
  hubDelete<LoSiteSslStatus>('/site/custom-domain/ssl/custom');

export type LoSiteGoogleOAuthStatus = {
  configured: boolean;
  clientId: string | null;
  clientSecretConfigured: boolean;
  redirectUri: string;
};

export const fetchLoSiteGoogleOAuth = () =>
  hubGet<LoSiteGoogleOAuthStatus>('/site/google-oauth');

export const updateLoSiteGoogleOAuth = (data: {
  clientId?: string | null;
  clientSecret?: string | null;
  clear?: boolean;
}) => hubPut<LoSiteGoogleOAuthStatus>('/site/google-oauth', data);

export const fetchLoPlanUsage = () => hubGet<LoPlanUsage>('/plan-usage');

export type LoBillingPlanId = 'discovery' | 'creator' | 'expert';

export type LoBillingSummary = {
  planId: LoBillingPlanId;
  planLabel: string;
  subscription: {
    id: string;
    status: string;
    planType: string;
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    stripeSubscriptionId: string | null;
  } | null;
  canManageBilling: boolean;
};

export type LoBillingPlan = {
  id: LoBillingPlanId;
  label: string;
  priceMonthly: string;
  priceAnnual: string | null;
  features: string[];
  stripeConfigured: boolean;
  stripeAnnualConfigured?: boolean;
};

export type LoBillingInvoice = {
  id: string;
  number: string | null;
  createdAt: string;
  amount: number;
  amountDue: number;
  currency: string;
  status: string;
  invoicePdf: string | null;
  hostedInvoiceUrl: string | null;
};

export const fetchLoBillingSummary = () => hubGet<LoBillingSummary>('/billing');
export const fetchLoBillingPlans = () => hubGet<LoBillingPlan[]>('/billing/plans');
export const fetchLoBillingInvoices = () =>
  hubGet<{ invoices: LoBillingInvoice[] }>('/billing/invoices').then((r) => r.invoices);
export const createLoBillingCheckout = (plan: 'creator' | 'expert', interval: 'monthly' | 'annual', returnUrl: string) =>
  hubPost<{ sessionId: string; url: string | null }>('/billing/checkout', { plan, interval, returnUrl });
export const createLoBillingPortal = (returnUrl: string) =>
  hubPost<{ url: string }>('/billing/portal', { returnUrl });
export const cancelLoBillingSubscription = () => hubPost<{ ok: boolean }>('/billing/cancel');

export const fetchLoBioLinks = () => hubGet<Array<{
  id: string;
  label: string;
  url: string;
  linkType: string;
  targetId?: string | null;
  isActive: boolean;
  order: number;
  clickCount: number;
}>>('/bio-links');

export interface LoBioLinkTargets {
  siteSlug: string | null;
  formations: Array<{ id: string; title: string; isPublished: boolean; funnelSlug: string | null }>;
  funnels: Array<{ id: string; slug: string; status: string; title: string }>;
  bookingTypes: Array<{ id: string; name: string; durationMin: number }>;
  communities: Array<{ id: string; name: string; slug: string; status: string }>;
}

export const fetchLoBioLinkTargets = () => hubGet<LoBioLinkTargets>('/bio-links/targets');

export const createLoBioLink = (data: {
  label?: string;
  url?: string;
  linkType?: 'url' | 'funnel' | 'formation' | 'booking' | 'community';
  targetId?: string | null;
}) => hubPost('/bio-links', data);

export const reorderLoBioLinks = (linkIds: string[]) =>
  hubPut('/bio-links/reorder', { linkIds });
export const deleteLoBioLink = (id: string) => hubDelete(`/bio-links/${id}`);

export const fetchLoPipeline = () => hubGet<{
  id: string;
  stages: Array<{
    id: string;
    name: string;
    order: number;
    color: string;
    deals: Array<{ id: string; title: string; value: number; contact?: LoContact | null }>;
  }>;
}>('/crm/pipeline');
export const createLoDeal = (data: { stageId: string; title: string; value?: number }) =>
  hubPost('/crm/deals', data);
export const moveLoDeal = (id: string, stageId: string) =>
  hubPatch(`/crm/deals/${id}/move`, { stageId });
export const updateLoDeal = (id: string, data: { title?: string; value?: number; notes?: string }) =>
  hubPatch(`/crm/deals/${id}`, data);
export const deleteLoDeal = (id: string) =>
  hubDelete<{ deleted: boolean }>(`/crm/deals/${id}`);
export const fetchLoDeals = () =>
  hubGet<Array<{ id: string; title: string; value: number; notes?: string | null; stage: { name: string }; contact?: LoContact | null }>>('/crm/deals');
export const exportLoDealsCsv = () =>
  downloadLyrraOneBlob('/crm/deals/export', `crm-deals-${Date.now()}.csv`, 'text/csv;charset=utf-8');
export const createLoCrmStage = (data: { name: string; color?: string }) =>
  hubPost('/crm/stages', data);
export const updateLoCrmStage = (id: string, data: { name?: string; color?: string }) =>
  hubPatch(`/crm/stages/${id}`, data);
export const deleteLoCrmStage = (id: string) => hubDelete(`/crm/stages/${id}`);

export const fetchLoAffiliate = () => hubGet<{
  id: string;
  commissionPct: number;
  cookieDays: number;
  isActive: boolean;
  pageSettings?: import('@/lib/lyrraone-affiliate-page').LoAffiliatePageSettings;
  programSettings?: import('@/lib/lyrraone-affiliate-program-settings').LoAffiliateProgramSettings;
  affiliates: Array<{
    id: string;
    email: string;
    name?: string | null;
    code: string;
    isActive: boolean;
    profile?: Record<string, unknown>;
    stats?: Record<string, number>;
  }>;
}>('/affiliate');
export const updateLoAffiliate = (data: {
  commissionPct?: number;
  cookieDays?: number;
  isActive?: boolean;
  pageSettings?: import('@/lib/lyrraone-affiliate-page').LoAffiliatePageSettings;
  programSettings?: import('@/lib/lyrraone-affiliate-program-settings').LoAffiliateProgramSettings;
}) => hubPatch('/affiliate', data);
export const addLoAffiliate = (data: { email: string; name?: string }) =>
  hubPost('/affiliate/members', data);

export type LoAffiliatePayoutAffiliate = {
  id: string;
  email: string;
  name?: string | null;
  code: string;
  isActive: boolean;
  stats: Record<string, number>;
  salesCount: number;
  totalCommission: number;
  paidOut: number;
  pendingPayout: number;
  payouts: Array<{
    id: string;
    amount: number;
    status: string;
    note?: string | null;
    paidAt?: string | null;
    createdAt: string;
  }>;
};

export const fetchLoAffiliatePayouts = () => hubGet<LoAffiliatePayoutAffiliate[]>('/affiliate/payouts');
export const recordLoAffiliatePayout = (data: { affiliateId: string; amount: number; note?: string; viaStripe?: boolean }) =>
  hubPost('/affiliate/payouts', data);

export const fetchLoBookingTypes = () => hubGet<Array<{ id: string; name: string; slug?: string | null; durationMin: number; price: number; isActive: boolean; bufferMin?: number; availability?: Array<{ dayOfWeek: number; startHour: number; startMin?: number; endHour: number; endMin?: number }>; _count?: { bookings: number } }>>('/booking/types');
export const fetchLoBookingSettings = () =>
  hubGet<{
    availability: Array<{ dayOfWeek: number; startHour: number; startMin?: number; endHour: number; endMin?: number }>;
    slotDays?: number;
    timeZone?: string;
    postRedirectMode?: 'creator' | 'custom';
    postRedirectCustomUrl?: string;
  }>('/booking/settings');
export const updateLoBookingSettings = (data: {
  availability?: Array<{ dayOfWeek: number; startHour: number; startMin?: number; endHour: number; endMin?: number }>;
  slotDays?: number;
  timeZone?: string;
  postRedirectMode?: 'creator' | 'custom';
  postRedirectCustomUrl?: string;
}) => hubPatch('/booking/settings', data);
export const createLoBookingType = (data: { name: string; durationMin?: number; price?: number; bufferMin?: number; availability?: Array<{ dayOfWeek: number; startHour: number; startMin?: number; endHour: number; endMin?: number }> }) =>
  hubPost('/booking/types', data);
export const updateLoBookingType = (typeId: string, data: Partial<{ name: string; durationMin: number; price: number; description: string | null; isActive: boolean; bufferMin: number; availability: Array<{ dayOfWeek: number; startHour: number; startMin?: number; endHour: number; endMin?: number }> }>) =>
  hubPatch(`/booking/types/${typeId}`, data);
export const approveLoAffiliate = (affiliateId: string) =>
  hubPatch(`/affiliate/members/${affiliateId}/approve`, {});
export const fetchLoBookings = () => hubGet<Array<{ id: string; guestEmail: string; guestName?: string | null; startsAt: string; status: string; bookingType: { name: string } }>>('/booking/appointments');
export const deleteLoBooking = (bookingId: string) => hubDelete<{ deleted: boolean }>(`/booking/appointments/${bookingId}`);
export const sendLoBookingReminderEmail = (
  bookingId: string,
  data: { subject: string; htmlBody: string },
) => hubPost<{ sent: boolean; channel: 'email' }>(`/booking/appointments/${bookingId}/reminder/email`, data);
export const sendLoBookingReminderSms = (
  bookingId: string,
  data: { body: string },
) => hubPost<{ sent: boolean; channel: 'sms' }>(`/booking/appointments/${bookingId}/reminder/sms`, data);

export const fetchLoGoogleCalendarStatus = () =>
  hubGet<{
    configured: boolean;
    connected: boolean;
    accountEmail?: string | null;
    calendarId?: string;
    calendarSummary?: string | null;
    connectedAt?: string | null;
    redirectUri?: string | null;
  }>('/booking/google/status');
export const fetchLoGoogleCalendarAuthUrl = () => hubGet<{ url: string }>('/booking/google/auth-url');
export const fetchLoGoogleCalendars = () =>
  hubGet<Array<{ id: string; summary: string; primary: boolean }>>('/booking/google/calendars');
export const updateLoGoogleCalendar = (data: { calendarId: string; calendarSummary?: string }) =>
  hubPatch('/booking/google/calendar', data);
export const disconnectLoGoogleCalendar = () => hubDelete<{ disconnected: boolean }>('/booking/google');

export type LoGoogleCalendarEventRange = 'day' | 'week' | 'month';

export type LoGoogleCalendarEvent = {
  id: string;
  title: string;
  startsAt: string | null;
  endsAt: string | null;
  allDay: boolean;
  htmlLink: string | null;
  location: string | null;
};

export const fetchLoGoogleCalendarEvents = (range: LoGoogleCalendarEventRange = 'week') =>
  hubGet<{
    connected: boolean;
    range: LoGoogleCalendarEventRange;
    calendarSummary?: string | null;
    events: LoGoogleCalendarEvent[];
  }>(`/booking/google/events?range=${encodeURIComponent(range)}`);

export const fetchLoCommunities = () => hubGet<Array<{ id: string; slug: string; name: string; status: string; accessType: string; price: number; _count?: { members: number; posts: number } }>>('/communities');
export const createLoCommunity = (data: { name: string; description?: string; accessType?: string; price?: number; storeFormationId?: string }) =>
  hubPost('/communities', data);
export const updateLoCommunity = (id: string, data: Partial<{
  name: string;
  description: string;
  accessType: string;
  price: number;
  storeFormationId: string | null;
  status: 'draft' | 'published';
  primaryColor: string | null;
  secondaryColor: string | null;
  heroImageUrl: string | null;
  showMemberCount: boolean;
  showPostCount: boolean;
  charter: string | null;
}>) =>
  hubPatch(`/communities/${id}`, data);
export const fetchLoCommunity = (id: string) => hubGet(`/communities/${id}`);

export type LoCommunityRoom = {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  heroImageUrl?: string | null;
  sortOrder: number;
  _count?: { messages: number };
};

export const fetchLoCommunityRooms = (communityId: string) =>
  hubGet<LoCommunityRoom[]>(`/communities/${communityId}/rooms`);

export const createLoCommunityRoom = (
  communityId: string,
  data: { title: string; description?: string; heroImageUrl?: string | null },
) => hubPost(`/communities/${communityId}/rooms`, data);

export const updateLoCommunityRoom = (
  communityId: string,
  roomId: string,
  data: Partial<{ title: string; description: string | null; heroImageUrl: string | null; sortOrder: number }>,
) => hubPatch(`/communities/${communityId}/rooms/${roomId}`, data);

export const deleteLoCommunityRoom = (communityId: string, roomId: string) =>
  hubDelete(`/communities/${communityId}/rooms/${roomId}`);

export type LoWebinarReplayMessage = { atSec: number; author: string; text: string };

export const fetchLoWebinars = () => hubGet<Array<{
  id: string;
  slug: string;
  name: string;
  status: string;
  videoUrl?: string | null;
  hlsUrl?: string | null;
  offerUrl?: string | null;
  replayHours?: number;
  offerClicks?: number;
  registrationCount?: number;
  attendedCount?: number;
  conversionRate?: number;
  schedule?: Array<{ dayOfWeek: number; hour: number; minute: number }>;
  replayChat?: LoWebinarReplayMessage[];
  stats?: { registrations?: number; attended?: number; offerClicks?: number };
  _count?: { registrations: number };
}>>('/webinars');
export const createLoWebinar = (data: { name: string; videoUrl?: string; hlsUrl?: string; offerUrl?: string }) =>
  hubPost('/webinars', data);
export const fetchLoWebinarRegistrations = (id: string) =>
  hubGet<{ registrations: Array<{ id: string; email: string; name: string | null; scheduledAt: string | null; attended: boolean; createdAt: string }> }>(
    `/webinars/${id}/registrations`,
  ).then((r) => r.registrations ?? []);
export const fetchLoWebinar = (id: string) => hubGet(`/webinars/${id}`);
export const updateLoWebinar = (id: string, data: Partial<{
  name: string;
  videoUrl: string;
  hlsUrl: string | null;
  offerUrl: string;
  status: 'draft' | 'published';
  replayHours: number;
  schedule: Array<{ dayOfWeek: number; hour: number; minute: number }>;
  replayChat: LoWebinarReplayMessage[];
}>) =>
  hubPatch(`/webinars/${id}`, data);
export const publishLoWebinar = (id: string) => hubPatch(`/webinars/${id}`, { status: 'published' });

export const fetchLoPhysicalProducts = () => hubGet<Array<{
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  price: number;
  stock: number;
  isActive: boolean;
  variants?: Array<{ label: string }>;
  shipping?: { mode?: string; amount?: number; freeAbove?: number | null };
}>>('/physical-products');
export const createLoPhysicalProduct = (data: {
  name: string;
  description?: string;
  price?: number;
  stock?: number;
  imageUrl?: string;
  variants?: Array<{ label: string }>;
  shipping?: { mode?: 'flat' | 'free'; amount?: number; freeAbove?: number | null };
}) => hubPost('/physical-products', data);
export const updateLoPhysicalProduct = (id: string, data: Partial<{
  name: string;
  description: string;
  price: number;
  stock: number;
  isActive: boolean;
  imageUrl: string | null;
  variants: Array<{ label: string }>;
  shipping: { mode?: 'flat' | 'free'; amount?: number; freeAbove?: number | null };
}>) => hubPatch(`/physical-products/${id}`, data);

export interface LoPhysicalOrder {
  id: string;
  status: string;
  subtotal: number;
  shippingAmount: number;
  total: number;
  funnelSlug?: string | null;
  shippingAddress: Record<string, string>;
  createdAt: string;
  items: Array<{ productName: string; quantity: number; unitPrice: number; variantLabel?: string | null }>;
  buyer: { email: string; firstName?: string | null; lastName?: string | null };
}

export const fetchLoPhysicalOrders = () => hubGet<LoPhysicalOrder[]>('/physical-orders');
export const updateLoPhysicalOrderStatus = (id: string, status: 'paid' | 'shipped' | 'delivered' | 'cancelled') =>
  hubPatch(`/physical-orders/${id}/status`, { status });

import type { LoSmsAudience, LoSmsRecipientMode } from '@/lib/lyrraone-sms-audience';

export type { LoSmsAudience, LoSmsRecipientMode };

export interface LoSmsCampaign {
  id: string;
  name: string;
  body: string;
  status: string;
  recipientCount: number;
  sentAt?: string | null;
  audience?: LoSmsAudience | null;
  marketing?: boolean;
  sender?: string | null;
  scheduledAt?: string | null;
  smsfactorTicket?: string;
  stats?: {
    sent?: number;
    failed?: number;
    total?: number;
    cost?: number;
    credits?: number;
    recipientMode?: string;
    blacklisted?: number;
    invalid?: number;
  };
}

export type LoSmsSendOptions = LoSmsAudience;

export type LoSmsCampaignInput = {
  name: string;
  body: string;
  audience?: LoSmsAudience;
  marketing?: boolean;
  sender?: string;
  scheduledAt?: string | null;
};

export const fetchLoSms = () => hubGet<LoSmsCampaign[]>('/sms');
export const createLoSms = (data: LoSmsCampaignInput) => hubPost<LoSmsCampaign>('/sms', data);
export const updateLoSms = (id: string, data: Partial<LoSmsCampaignInput>) =>
  hubPatch<LoSmsCampaign>(`/sms/${encodeURIComponent(id)}`, data);
export const deleteLoSms = (id: string) => hubDelete<{ deleted: boolean }>(`/sms/${encodeURIComponent(id)}`);
export const sendLoSms = (id: string, options?: LoSmsSendOptions) =>
  hubPost<LoSmsCampaign>(`/sms/${id}/send`, options ?? undefined);

export interface LoSmsSettings {
  provider: 'smsfactor';
  sender: string;
  hasApiToken: boolean;
  configured: boolean;
}

export const fetchLoSmsSettings = () => hubGet<LoSmsSettings>('/sms/settings');
export const updateLoSmsSettings = (data: {
  apiToken?: string;
  sender?: string;
}) => hubPut<LoSmsSettings>('/sms/settings', data);
export const testLoSmsSettings = (to: string) => hubPost<{ ok: boolean }>('/sms/settings/test', { to });

export interface LoPromotion {
  id: string;
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUses: number | null;
  usedCount: number;
  validUntil: string | null;
  formationId: string | null;
  isActive: boolean;
  createdAt: string;
}

export const fetchLoPromotions = () => hubGet<LoPromotion[]>('/promotions');
export const createLoPromotion = (data: {
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  maxUses?: number | null;
  validUntil?: string | null;
  formationId?: string | null;
}) => hubPost<LoPromotion>('/promotions', data);
export const updateLoPromotion = (id: string, data: {
  isActive?: boolean;
  maxUses?: number | null;
  validUntil?: string | null;
}) => hubPatch<LoPromotion>(`/promotions/${id}`, data);
export const deleteLoPromotion = (id: string) => hubDelete<{ deleted: boolean }>(`/promotions/${id}`);

export interface LoSellerPaymentSettings {
  planTier: 'discovery' | 'creator' | 'expert';
  allowedMethods: { lyrrapay: boolean; stripe: boolean };
  requiredMethod: 'lyrrapay' | 'stripe' | null;
  preferredMethod: 'lyrrapay' | 'stripe';
  stripeConnect: { accountId: string | null; chargesEnabled: boolean };
}

export const fetchLoSellerPayment = () => hubGet<LoSellerPaymentSettings>('/seller-payment');
export const updateLoSellerPayment = (method: 'lyrrapay' | 'stripe') =>
  hubPut<LoSellerPaymentSettings>('/seller-payment', { method });
export const onboardLoStripeConnect = (returnUrl: string, refreshUrl: string) =>
  hubPost<{ url: string; accountId: string }>('/seller-payment/stripe/onboard', { returnUrl, refreshUrl });
export const syncLoStripeConnect = () =>
  hubPost<{ chargesEnabled: boolean; accountId: string | null }>('/seller-payment/stripe/sync');

export interface LoSellerBillingProfile {
  legalName: string;
  tradeName?: string;
  addressLine1: string;
  addressLine2?: string;
  postalCode: string;
  city: string;
  country: string;
  siret?: string;
  vatNumber?: string;
  billingEmail?: string;
}

export interface LoSellerBillingResponse {
  profile: LoSellerBillingProfile | null;
  billingEmail: string;
  complete: boolean;
  missing: string[];
}

export const fetchLoSellerBilling = () => hubGet<LoSellerBillingResponse>('/seller-billing');
export const updateLoSellerBilling = (data: LoSellerBillingProfile) =>
  hubPut<LoSellerBillingResponse>('/seller-billing', data);

export type LoSellerSalesPeriod = '7d' | '30d' | '90d' | '365d' | 'all';

export interface LoSellerSaleRow {
  id: string;
  type: 'formation' | 'bundle' | 'podcast';
  productId: string;
  productTitle: string;
  buyer: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  amount: number;
  platformFee: number;
  netAmount: number;
  paymentProvider: string | null;
  paymentChannel: 'stripe' | 'lyrrapay' | 'other';
  status: string;
  createdAt: string;
  affiliate: {
    id: string;
    code: string;
    name: string | null;
    commissionPct: number;
    commissionCents: number;
  } | null;
}

export interface LoSellerSalesResponse {
  summary: {
    totalSales: number;
    totalGrossCents: number;
    totalPlatformFeeCents: number;
    totalNetCents: number;
    byPayment: {
      stripe: { count: number; grossCents: number; platformFeeCents: number };
      lyrrapay: { count: number; grossCents: number; platformFeeCents: number };
      other: { count: number; grossCents: number; platformFeeCents: number };
    };
    paymentSettings: {
      planTier: string;
      preferredMethod: 'stripe' | 'lyrrapay';
      effectiveMethod: 'stripe' | 'lyrrapay';
      stripeConnected: boolean;
      stripeChargesEnabled: boolean;
    };
  };
  sales: LoSellerSaleRow[];
  total: number;
  limit: number;
  offset: number;
  period: LoSellerSalesPeriod;
}

export const fetchLoSellerSales = (params?: {
  limit?: number;
  offset?: number;
  period?: LoSellerSalesPeriod;
}) => hubGet<LoSellerSalesResponse>('/sales', params);

export async function exportLoSalesCsv(): Promise<void> {
  await downloadLyrraOneBlob('/sales/export', `ventes-lyrraone-${Date.now()}.csv`, 'text/csv;charset=utf-8');
}

export const LYRRAONE_HUB_MODULES = [
  { key: 'hub', path: '/marketing', label: 'Vue d\'ensemble', icon: 'LayoutDashboard', desc: '', group: '' },
  { key: 'contacts', path: '/marketing/contacts', label: 'Contacts', icon: 'Users', desc: 'Gérez vos prospects et clients', group: 'acquisition' },
  { key: 'email', path: '/marketing/email', label: 'Email marketing', icon: 'Mail', desc: 'Campagnes et séquences automatisées', group: 'acquisition' },
  { key: 'automations', path: '/marketing/automations', label: 'Automatisations', icon: 'Zap', desc: 'Workflows déclenchés automatiquement', group: 'acquisition' },
  { key: 'funnels', path: '/funnels', label: 'Tunnels de vente', icon: 'Megaphone', desc: 'Pages de capture et parcours d\'achat', group: 'vente' },
  { key: 'bundles', path: '/marketing/formation-bundles', label: 'Packs formations', icon: 'Package', desc: 'Vendez plusieurs formations en bundle', group: 'vente' },
  { key: 'payments', path: '/marketing/payments', label: 'Encaissement', icon: 'CreditCard', desc: 'LyrraPay ou votre compte Stripe Connect', group: 'vente' },
  { key: 'site', path: '/marketing/site', label: 'Site web', icon: 'Globe', desc: 'Votre site vitrine personnalisable', group: 'vente' },
  { key: 'blog', path: '/marketing/site/blog', label: 'Blog', icon: 'Newspaper', desc: 'Articles SEO pour votre site vitrine', group: 'vente' },
  { key: 'creator-page', path: '/marketing/creator-page', label: 'Page créateur', icon: 'Link', desc: 'Votre profil public avec vos liens', group: 'vente' },
  { key: 'crm', path: '/marketing/crm', label: 'Pipeline CRM', icon: 'Kanban', desc: 'Suivez vos opportunités commerciales', group: 'vente' },
  { key: 'affiliate', path: '/marketing/affiliate', label: 'Affiliation', icon: 'Share2', desc: 'Programme d\'affiliation pour vos ventes', group: 'vente' },
  { key: 'promotions', path: '/marketing/promotions', label: 'Coupons', icon: 'Tag', desc: 'Codes promo et réductions', group: 'vente' },
  { key: 'booking', path: '/marketing/booking', label: 'Calendrier', icon: 'Calendar', desc: 'Prise de rendez-vous en ligne', group: 'engagement' },
  { key: 'communities', path: '/marketing/communities', label: 'Communautés', icon: 'MessageCircle', desc: 'Espaces d\'échange avec vos apprenants', group: 'engagement' },
  { key: 'webinars', path: '/marketing/webinars', label: 'Webinaires', icon: 'Presentation', desc: 'Live et automatiques — inscrits et participants', group: 'engagement' },
  { key: 'physical', path: '/marketing/physical-products', label: 'Produits physiques', icon: 'Package', desc: 'Vendez des produits complémentaires', group: 'engagement' },
  { key: 'sms', path: '/marketing/sms', label: 'SMS', icon: 'Smartphone', desc: 'Messages texte à vos contacts', group: 'acquisition' },
] as const;

/* ——— Équipe compte formateur ——— */

export type LoTeamMember = {
  id: string;
  ownerId: string;
  userId: string | null;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  menuPermissions: Record<string, boolean>;
  invitedAt: string;
  acceptedAt: string | null;
  createdAt: string;
  user: { id: string; firstName: string; lastName: string; email: string; avatarUrl: string | null } | null;
};

export type LoTeamPermissionsCatalog = {
  keys: string[];
  labels: Record<string, string>;
  groups: Array<{ id: string; title: string; keys: readonly string[] }>;
  defaults: Record<string, boolean>;
};

export const fetchLoTeamPermissionsCatalog = () =>
  hubGet<LoTeamPermissionsCatalog>('/team/permissions-catalog');

export const fetchLoTeamMembers = () => hubGet<LoTeamMember[]>('/team/members');

export const inviteLoTeamMember = (data: {
  email: string;
  firstName?: string;
  lastName?: string;
  password?: string;
  createAccount?: boolean;
  menuPermissions?: Record<string, boolean>;
}) => hubPost<{
  member: LoTeamMember;
  temporaryPassword: string | null;
  inviteUrl: string | null;
  inviteToken: string | null;
}>('/team/members', data);

export const updateLoTeamMember = (
  id: string,
  data: { firstName?: string; lastName?: string; menuPermissions?: Record<string, boolean> },
) => hubPatch<LoTeamMember>(`/team/members/${id}`, data);

export const revokeLoTeamMember = (id: string) =>
  hubDelete<{ revoked: boolean }>(`/team/members/${id}`);

export const fetchLoTeamInvitePreview = (token: string) =>
  hubGet<{ email: string; firstName: string; lastName: string; ownerName: string }>(
    `/team/invites/${encodeURIComponent(token)}`,
  );

export const acceptLoTeamInvite = (
  token: string,
  data: { password?: string; firstName?: string; lastName?: string },
) => hubPost<LoTeamMember>(`/team/invites/${encodeURIComponent(token)}/accept`, data);
